import {
  FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Box } from "@mui/material"
import { StaffSchedule } from "../../interfaces"
import { Brush, useRowWindow } from "../../hooks"
import { DayKey } from "../../model/mskCalendar"
import { DayColumn, HeaderTiers, buildHeaderTiers } from "../../model/period"
import { CellKey, ScheduleMap } from "../../model/scheduleCells"
import {
  StrokeCommit,
  StrokeEvent,
  StrokeState,
  strokeRange,
  strokeReducer,
} from "../../model/strokeMachine"
import { GridHeader } from "./gridHeader"
import { GridRow } from "./gridRow"
import { GridTotalsRow } from "./gridTotalsRow"
import {
  HEADER_H,
  ROW_H,
  ROW_OVERSCAN,
  gridBorderColorStrong,
  gridWidth,
} from "./gridGeometry"

interface ScheduleGridProps {
  staff: StaffSchedule.Staff[]
  days: DayColumn[]
  cells: ScheduleMap
  legend: Map<number, StaffSchedule.ShiftType>
  pendingKeys: ReadonlySet<CellKey>
  totalsByStaff: Map<number, number>
  totalsByDay: Map<DayKey, number>
  grandTotal: number
  brush: Brush
  canWrite: boolean
  onStrokeCommit: (stroke: StrokeCommit) => void
  onClearBrush: () => void
}

/**
 * Один скролл-контейнер: шапка и колонки залипают на CSS, scrollLeft ни с чем не синхронизируется.
 * Грид владеет состоянием мазка (strokeMachine) и глобальными слушателями указателя.
 */
export const ScheduleGrid: FC<ScheduleGridProps> = ({
  staff,
  days,
  cells,
  legend,
  pendingKeys,
  totalsByStaff,
  totalsByDay,
  grandTotal,
  brush,
  canWrite,
  onStrokeCommit,
  onClearBrush,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const tiers: HeaderTiers = useMemo(() => buildHeaderTiers(days), [days])
  const rowWindow = useRowWindow({
    scrollRef,
    rowHeight: ROW_H,
    rowCount: staff.length,
    headerHeight: HEADER_H,
    overscan: ROW_OVERSCAN,
  })

  // Состояние мазка: ref — для глобальных слушателей (они не пересоздаются),
  // state — для превью диапазона.
  const [strokeState, setStrokeState] = useState<StrokeState>({ kind: "idle" })
  const strokeRef = useRef(strokeState)
  const commitRef = useRef(onStrokeCommit)
  useEffect(() => {
    commitRef.current = onStrokeCommit
  }, [onStrokeCommit])
  const clearBrushRef = useRef(onClearBrush)
  useEffect(() => {
    clearBrushRef.current = onClearBrush
  }, [onClearBrush])

  const dispatchStroke = useCallback((event: StrokeEvent) => {
    const res = strokeReducer(strokeRef.current, event)
    strokeRef.current = res.state
    setStrokeState(res.state)
    if (res.commit) commitRef.current(res.commit)
  }, [])

  // Глобальные up/cancel/Esc: отпускание кнопки или Esc вне грида не должны оставить
  // залипший мазок, который продолжит расти при следующем движении мыши.
  useEffect(() => {
    const up = () => dispatchStroke({ type: "up" })
    const cancel = () => dispatchStroke({ type: "cancel" })
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      // Esc с приоритетом: мазок в процессе — отменяется мазок, кисть остаётся;
      // мазка нет — сбрасывается кисть.
      if (strokeRef.current.kind === "painting") {
        dispatchStroke({ type: "cancel" })
      } else {
        clearBrushRef.current()
      }
    }
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", cancel)
    window.addEventListener("keydown", key)
    return () => {
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", cancel)
      window.removeEventListener("keydown", key)
    }
  }, [dispatchStroke])

  // Стабилизировано useCallback — иначе React.memo строк и ячеек бесполезен.
  const handleCellPointerDown = useCallback(
    (staffId: number, colIdx: number, e: ReactPointerEvent<HTMLElement>) => {
      // В браузере отбрасываем явно не-primary кнопку. В jsdom без PointerEvent поле button
      // может отсутствовать у fireEvent.pointerDown — такой тестовый клик считаем primary.
      if (!brush || (typeof e.button === "number" && e.button !== 0)) return
      // Браузер неявно захватывает указатель на pointerdown (касание/перо) — из-за захвата
      // pointerenter на соседних ячейках не доставляется. Снимаем захват; проверка
      // обязательна: в jsdom 23 этих методов нет, без неё тесты падают.
      const el = e.currentTarget
      if (el.hasPointerCapture?.(e.pointerId)) {
        el.releasePointerCapture(e.pointerId)
      }
      dispatchStroke({ type: "down", staffId, colIdx })
    },
    [brush, dispatchStroke],
  )
  const handleCellPointerEnter = useCallback(
    (staffId: number, colIdx: number) =>
      dispatchStroke({ type: "enter", staffId, colIdx }),
    [dispatchStroke],
  )

  const activeRange = strokeRange(strokeState)
  const paintingStaffId =
    strokeState.kind === "painting" ? strokeState.staffId : null
  const visibleStaff = staff.slice(rowWindow.start, rowWindow.end)

  return (
    <Box
      ref={scrollRef}
      sx={{
        overflow: "auto",
        height: "100%",
        userSelect: "none",
        // Грид по построению шире экрана: контейнер панорамируется по обеим осям.
        // На тач-устройствах горизонтальный жест — скролл (придёт pointercancel),
        // поэтому протяжка пальцем не поддерживается, только тап (спека, разделы 2, 9).
        touchAction: "pan-x pan-y",
        cursor: brush ? "crosshair" : "default",
        border: "1px solid",
        borderColor: gridBorderColorStrong,
        borderRadius: 1,
      }}
    >
      <Box sx={{ width: gridWidth(days.length), minHeight: "100%" }}>
        <GridHeader days={days} tiers={tiers} />
        <div style={{ height: rowWindow.topSpacer }} />
        {visibleStaff.map((s) => (
          <GridRow
            key={s.id}
            staff={s}
            days={days}
            cells={cells}
            legend={legend}
            strokeRange={paintingStaffId === s.id ? activeRange : null}
            pendingKeys={pendingKeys}
            total={totalsByStaff.get(s.id) ?? 0}
            canWrite={canWrite}
            onCellPointerDown={handleCellPointerDown}
            onCellPointerEnter={handleCellPointerEnter}
          />
        ))}
        <div style={{ height: rowWindow.bottomSpacer }} />
        <GridTotalsRow
          days={days}
          totalsByDay={totalsByDay}
          grandTotal={grandTotal}
        />
      </Box>
    </Box>
  )
}
